import { useQuery } from "@apollo/client";
import { gql } from "@apollo/client";
import { Link } from "react-router-dom";
import { Text } from "@nbs/ui";
import { NbsChartContainer } from "@/features/charts/NbsChartContainer";
import { NbsGroupedBarChart } from "@/features/charts/NbsGroupedBarChart";
import { NbsHorizontalBarChart } from "@/features/charts/NbsHorizontalBarChart";

const N2_SERIES_QUERY = gql`
  query N2IndicatorAnalytics($indicateurId: ID!) {
    indicatorSeries(indicateurId: $indicateurId) {
      indicateurId
      libelle
      unit
      points {
        mesureId
        periodStart
        value
        unit
        territoryLabel
        dimensions {
          type
          label
        }
      }
    }
    indicatorDistribution(indicateurId: $indicateurId) {
      buckets {
        modalityLabel
        value
        unit
        mesureId
      }
    }
  }
`;

export function N2IndicatorSeriesPanel({ indicateurId }: { readonly indicateurId: string }) {
  const { data, loading, error } = useQuery(N2_SERIES_QUERY, {
    variables: { indicateurId },
  });
  const series = data?.indicatorSeries;
  const dist = data?.indicatorDistribution;
  const points = series?.points ?? [];
  const hasModalities = (dist?.buckets?.length ?? 0) > 0;

  type Point = {
    mesureId: string;
    periodStart: string;
    value: number;
    territoryLabel: string;
    dimensions?: Array<{ type: string; label: string }>;
  };
  const typedPoints = points as Point[];
  const byYearTerritory = (() => {
    const years = [...new Set(typedPoints.map((p) => p.periodStart.slice(0, 4)))].sort();
    const territories = [...new Set(typedPoints.map((p) => p.territoryLabel))];
    if (years.length <= 1 && !typedPoints.some((p) => (p.dimensions?.length ?? 0) > 0)) {
      return null;
    }
    return years.map((year) => {
      const row: Record<string, string | number> = { year };
      for (const t of territories) {
        const hit = typedPoints.find(
          (p) => p.periodStart.startsWith(year) && p.territoryLabel === t,
        );
        if (hit) row[t] = hit.value;
      }
      return row;
    });
  })();

  return (
    <div className="nbs-n2-analytics">
      {error ? (
        <Text role="alert" className="nbs-text--error">
          {error.message}
        </Text>
      ) : null}
      {hasModalities ? (
        <NbsChartContainer
          title="Distribution"
          unit="%"
          isLoading={loading}
          empty={!loading && !dist.buckets.length}
        >
          <NbsHorizontalBarChart
            data={dist.buckets.map((b: { modalityLabel: string; value: number }) => ({
              label: b.modalityLabel,
              value: b.value,
            }))}
          />
        </NbsChartContainer>
      ) : null}
      {byYearTerritory && byYearTerritory.length > 0 ? (
        <NbsChartContainer
          title="Série / comparaison"
          unit={series?.unit}
          isLoading={loading}
          empty={!loading && points.length === 0}
        >
          <NbsGroupedBarChart
            data={byYearTerritory}
            xKey="year"
            seriesKeys={Object.keys(byYearTerritory[0] ?? {}).filter((k) => k !== "year")}
          />
        </NbsChartContainer>
      ) : null}
      {points[0] ? (
        <Text>
          <Link to={`/measures?indicatorId=${indicateurId}`}>
            Voir toutes les mesures ({points.length})
          </Link>
          {" · "}
          <Link
            to={`/n3?mesureIds=${points
              .map((p: { mesureId: string }) => p.mesureId)
              .slice(0, 20)
              .join(",")}`}
          >
            Ouvrir N3
          </Link>
        </Text>
      ) : null}
    </div>
  );
}
